import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    AdjustmentAccountOption,
    AdjustmentCreateEvent,
    AdjustmentEditEvent,
    AdjustmentRow,
} from '../../types/closing-adjustments.types';

/**
 * REV-21370 — `Neue Korrekturbuchung` and `Korrekturbuchung bearbeiten` (Pure View). Contract:
 * `e2e-lab/adjustment-modal.lab.spec.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ **ONE component with a `mode`, and that does NOT contradict ADR-0015 §9.**
 *
 * §9 refuses a `mode` for the two LISTS, and its reasoning is specific: a `mode` there would carry
 * two mutually exclusive column sets, two empty states and two footers, so the flag would only be a
 * name for "there are actually two components here". None of that applies to these two dialogs. They
 * share every field, every validation rule, the live `Buchungssatz` preview, the layout and the
 * footer; they differ in a HEADER, a BUTTON LABEL, and whether the optional `Zuordnung` field is
 * offered. Two files would be two places to keep one validation rule in step — which is the failure
 * §9 is trying to prevent, arriving from the other side.
 *
 * (The prototype's two dialogs also differ in how the accounts are picked — selects in one, free
 * numeric inputs in the other. That is prototype drift rather than a requirement: a free number field
 * invites an account that is not in the mandate's chart, which the backend then refuses. Both modes
 * use the picker.)
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * **Dumb**: it validates for the sake of the BUTTON, never as the boundary. Every rule below also
 * lives in the backend and is enforced there (ADR-0015 §6) — the point of having it here is that the
 * primary button can stay inactive until the form is complete, which is what AC5 asks for.
 *
 * ⚠️ **No agent surface** (§8): no confidence, no proposal state, no accept and no reject. A
 * correction booking is created by the user, and this dialog is the only place one comes into
 * existence.
 */

/** Minor units per major unit for a two-exponent currency. The only place this view multiplies. */
const MINOR_PER_MAJOR = 100;

@Component({
    selector: 'abcp-adjustment-modal',
    templateUrl: './adjustment-modal.component.html',
    styleUrls: ['./adjustment-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAdjustmentModalComponent implements OnChanges {
    /**
     * `create` = `Neue Korrekturbuchung`; `edit` = `Korrekturbuchung bearbeiten`. See the class doc.
     */
    @Input() mode: 'create' | 'edit' = 'create';

    /** The row being edited. Required for `edit`, ignored for `create`. */
    @Input() adjustment: AdjustmentRow | null = null;

    /** The closing's account plan. EMPTY renders the view's own "no accounts" state. */
    @Input() accounts: AdjustmentAccountOption[] = [];

    /** The closing's currency from its settings; only a label here, the backend stores it. */
    @Input() currency = '';

    /** A submit is in flight — the button spins and stops accepting clicks. */
    @Input() pending = false;

    /**
     * A refusal from the backend, as a translation KEY.
     *
     * Rendered rather than swallowed: the backend enforces the same rules and may refuse something
     * this form let through (a stale account plan, a year that has since closed), and the user has to
     * see why. A KEY and not a message — the backend never sends prose (AC13).
     */
    /**
     * The attribution a NEW correction opens with — the account whose pane the dialog was opened from.
     *
     * `null` on the closing-wide surface, where there is no account in context and the dialog opens on
     * `— automatisch (Soll) —` as before. Seeded from an account pane so the correction stays listed
     * where it was written: without it the attribution defaults to the DEBIT account, and a correction
     * entered in account 6800 with 6800 on the credit side would vanish from the list the moment it
     * was saved.
     *
     * Create mode only. `edit` reads the row's own attribution and must not be overwritten by the
     * surface the dialog happens to be open on.
     */
    @Input() defaultOwnerAccountId: string | null = null;

    /**
     * The balance sheet date, `YYYY-MM-DD`, used to pre-fill a NEW correction's booking date.
     *
     * `null` leaves the field empty and lets the server apply the same default. The view is given the
     * date rather than deriving it, because the fiscal year lives on the closing and a view that
     * guessed it would be a second, unenforced copy of that rule.
     */
    @Input() defaultDate: string | null = null;

    @Input() errorKey: string | null = null;

    @Output() submitted = new EventEmitter<AdjustmentCreateEvent | AdjustmentEditEvent>();
    @Output() cancelled = new EventEmitter<void>();

    /** The dialog panel — focused on open so the focus is trapped inside it (AC13). */
    @ViewChild('panel') panel?: ElementRef<HTMLElement>;

    // ── Form state. Transient, and the view owns it: AC12 names the open dialogs as display state. ──
    text = '';
    debitAccountId = '';
    creditAccountId = '';
    /** The amount from the `hamInput` number field — a number, or `null` while it is empty. */
    amount: number | null = null;
    /**
     * The booking date, `YYYY-MM-DD`, as the native date input holds it.
     *
     * Seeded from `defaultDate` on create and from the row on edit. Empty means "let the server
     * decide", which it answers with the balance sheet date — the view does not know that date and
     * must not invent it.
     */
    dateInput = '';
    ownerAccountId = '';
    remark = '';

    /** Built once per list, not per check: the dropdown re-renders on every new `options` array. */
    accountOptions: { id: string; label: string }[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['accounts'] !== undefined) {
            this.accountOptions = this.accounts.map((account) => ({ id: account.id, label: this.accountLabel(account) }));
        }
        // Pre-filled from the row on `edit`, blank on `create`. Driven by `ngOnChanges` rather than by
        // a setter, because the host RE-USES this component: it renders it with `*ngIf` and swaps the
        // row, so the form has to follow the input rather than be constructed once.
        if (changes['adjustment'] !== undefined || changes['mode'] !== undefined) {
            this.resetFromInputs();
        }
    }

    get isEdit(): boolean {
        return this.mode === 'edit';
    }

    get titleKey(): string {
        return this.isEdit ? 'abcp_adjustments_modal_edit_title' : 'abcp_adjustments_modal_create_title';
    }

    get submitLabelKey(): string {
        return this.isEdit ? 'abcp_adjustments_modal_save' : 'abcp_adjustments_modal_submit';
    }

    /**
     * The `Konto <nr> · <bezeichnung>` sub-header of the EDIT dialog.
     *
     * The Q10 attribution account, falling back to the debit account — which is what "no attribution"
     * means (see the entity). `null` renders no sub-header at all rather than an empty line.
     */
    get subtitle(): string | null {
        const account = this.adjustment?.owner ?? this.adjustment?.debit ?? null;

        return account === null ? null : `${account.number} · ${account.name}`;
    }

    /**
     * The `Zuordnung (Konto)` field — `create` only.
     *
     * The edit dialog does not offer it, exactly as the prototype has it: the attribution is what the
     * account area lists the correction under, and moving a correction between accounts is a different
     * gesture from correcting its text or its amount.
     */
    get showOwnerField(): boolean {
        return !this.isEdit;
    }

    get amountMinor(): number {
        const amount = this.amount;

        return amount !== null && Number.isFinite(amount) ? Math.round(amount * MINOR_PER_MAJOR) : 0;
    }

    /** Soll and Haben must differ (AC5) — and the user is TOLD, not merely blocked. */
    get hasSameAccounts(): boolean {
        return this.debitAccountId !== '' && this.debitAccountId === this.creditAccountId;
    }

    /**
     * Whether the primary button is active.
     *
     * ⚠️ This is comfort, never a boundary: every rule here is enforced in the backend, where it lives
     * (§6). A request that goes around this form is refused just the same.
     */
    get canSubmit(): boolean {
        return (
            !this.pending &&
            this.text.trim().length > 0 &&
            this.debitAccountId !== '' &&
            this.creditAccountId !== '' &&
            !this.hasSameAccounts &&
            this.amountMinor > 0
        );
    }

    /** The live `Buchungssatz: <soll> an <haben> · CHF <betrag>` preview (AC5). */
    get previewDebit(): string {
        return this.accountNumberOf(this.debitAccountId);
    }

    get previewCredit(): string {
        return this.accountNumberOf(this.creditAccountId);
    }

    /** `null` until an amount is entered — the preview then renders `—` rather than `CHF 0.00`. */
    get previewAmountMinor(): number | null {
        return this.amountMinor > 0 ? this.amountMinor : null;
    }

    accountLabel(account: AdjustmentAccountOption): string {
        return `${account.number} ${account.name}`;
    }

    onSubmit(): void {
        if (!this.canSubmit) {
            return;
        }

        const owner = this.ownerAccountId === '' ? null : this.ownerAccountId;
        const remark = this.remark.trim() === '' ? null : this.remark.trim();

        if (this.isEdit) {
            const adjustmentId = this.adjustment?.id ?? '';
            const edit: AdjustmentEditEvent = {
                adjustmentId,
                date: this.dateInput,
                text: this.text.trim(),
                debitAccountId: this.debitAccountId,
                creditAccountId: this.creditAccountId,
                amountMinor: this.amountMinor,
                // The edit dialog offers no attribution field, so it reports the row's existing one
                // unchanged rather than `null` — which would silently move the correction to its debit
                // account every time somebody fixed a typo.
                ownerAccountId: this.adjustment?.owner?.id ?? null,
                remark,
            };
            this.submitted.emit(edit);

            return;
        }

        const create: AdjustmentCreateEvent = {
            // ALWAYS `null` from this dialog: the field is not offered here, so there is nothing the
            // user could have chosen, and the server applies the balance sheet date. Sending today's
            // date instead would silently post the correction on the day it happened to be typed.
            date: null,
            text: this.text.trim(),
            debitAccountId: this.debitAccountId,
            creditAccountId: this.creditAccountId,
            amountMinor: this.amountMinor,
            currency: this.currency,
            ownerAccountId: owner,
            remark,
        };
        this.submitted.emit(create);
    }

    onCancel(): void {
        this.cancelled.emit();
    }

    /** `Esc` closes the dialog (AC13) — unless it just closed an open account list. */
    @HostListener('document:keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent): void {
        if (event.defaultPrevented) {
            return;
        }
        this.onCancel();
    }

    private accountNumberOf(accountId: string): string {
        return this.accounts.find((account) => account.id === accountId)?.number ?? '';
    }

    private resetFromInputs(): void {
        const row = this.adjustment;
        if (this.isEdit && row !== null) {
            this.text = row.text;
            this.debitAccountId = row.debit.id;
            this.creditAccountId = row.credit.id;
            this.amount = row.amount.minor / MINOR_PER_MAJOR;
            this.dateInput = row.date;
            this.ownerAccountId = row.owner?.id ?? '';
            this.remark = '';

            return;
        }

        this.text = '';
        this.debitAccountId = '';
        this.creditAccountId = '';
        this.amount = null;
        this.dateInput = this.defaultDate ?? '';
        // Empty string is the `Automatisch (Soll)` choice, which `onSubmit` reports as `null`.
        this.ownerAccountId = this.defaultOwnerAccountId ?? '';
        this.remark = '';
    }
}
